import { useState, useEffect, useRef, useCallback } from 'react';

export interface Song {
  id: string;
  title: string;
  artist?: string | null;
  url: string;
  duration?: number | null;
}

export interface AudioPlayerState {
  isPlaying: boolean;
  isFinished: boolean;
  currentSong: Song | null;
  currentTime: number;
  duration: number;
  volume: number;
  currentIndex: number;
  playlist: Song[];
}

export interface AudioPlayerControls {
  play: () => void;
  pause: () => void;
  stop: () => void;
  next: () => void;
  previous: () => void;
  setVolume: (volume: number) => void;
  seek: (time: number) => void;
  loadPlaylist: (songs: Song[], startIndex?: number) => void;
  loadSong: (song: Song) => void;
}

export function useAudioPlayer() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const stateRef = useRef<AudioPlayerState>({
    isPlaying: false,
    isFinished: false,
    currentSong: null,
    currentTime: 0,
    duration: 0,
    volume: 70,
    currentIndex: 0,
    playlist: [],
  });
  
  const [state, setState] = useState<AudioPlayerState>(stateRef.current);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    const audio = new Audio();
    audioRef.current = audio;

    const handleTimeUpdate = () => {
      setState(prev => ({
        ...prev,
        currentTime: audio.currentTime,
      }));
    };

    const handleLoadedMetadata = () => {
      setState(prev => ({
        ...prev,
        duration: audio.duration,
      }));
    };

    const handleEnded = () => {
      const currentState = stateRef.current;
      if (currentState.currentIndex < currentState.playlist.length - 1) {
        const nextIndex = currentState.currentIndex + 1;
        const nextSong = currentState.playlist[nextIndex];
        if (nextSong) {
          setState(prev => ({
            ...prev,
            currentIndex: nextIndex,
            currentSong: nextSong,
            currentTime: 0,
            duration: nextSong.duration || 0,
          }));
          audio.src = nextSong.url;
          audio.play().catch(err => console.error('Error playing next song:', err));
        }
      } else {
        setState(prev => ({
          ...prev,
          isPlaying: false,
          isFinished: true,
          currentTime: 0,
        }));
      }
    };

    const handleError = (e: ErrorEvent) => {
      console.error('Audio playback error:', e);
      setState(prev => ({
        ...prev,
        isPlaying: false,
      }));
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError as any);

    return () => {
      audio.pause();
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError as any);
      audio.src = '';
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = state.volume / 100;
    }
  }, [state.volume]);

  const loadSong = useCallback((song: Song) => {
    if (!audioRef.current) return;

    audioRef.current.src = song.url;
    setState(prev => ({
      ...prev,
      currentSong: song,
      currentTime: 0,
      duration: song.duration || 0,
      isFinished: false,
    }));
  }, []);

  const loadPlaylist = useCallback((songs: Song[], startIndex = 0) => {
    setState(prev => ({
      ...prev,
      playlist: songs,
      currentIndex: startIndex,
    }));

    if (songs.length > 0 && songs[startIndex]) {
      loadSong(songs[startIndex]);
    }
  }, [loadSong]);

  const play = useCallback(() => {
    if (!audioRef.current) return;

    audioRef.current.play()
      .then(() => {
        setState(prev => ({
          ...prev,
          isPlaying: true,
          isFinished: false,
        }));
      })
      .catch(err => {
        console.error('Error playing audio:', err);
      });
  }, []);

  const pause = useCallback(() => {
    if (!audioRef.current) return;

    audioRef.current.pause();
    setState(prev => ({
      ...prev,
      isPlaying: false,
    }));
  }, []);

  const stop = useCallback(() => {
    if (!audioRef.current) return;

    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setState(prev => ({
      ...prev,
      isPlaying: false,
      currentTime: 0,
    }));
  }, []);

  const next = useCallback(() => {
    const currentState = stateRef.current;
    const nextIndex = currentState.currentIndex + 1;
    if (nextIndex < currentState.playlist.length) {
      setState(prev => ({
        ...prev,
        currentIndex: nextIndex,
      }));
      loadSong(currentState.playlist[nextIndex]);
      if (currentState.isPlaying) {
        setTimeout(() => play(), 100);
      }
    }
  }, [loadSong, play]);

  const previous = useCallback(() => {
    const currentState = stateRef.current;
    const prevIndex = currentState.currentIndex - 1;
    if (prevIndex >= 0) {
      setState(prev => ({
        ...prev,
        currentIndex: prevIndex,
      }));
      loadSong(currentState.playlist[prevIndex]);
      if (currentState.isPlaying) {
        setTimeout(() => play(), 100);
      }
    }
  }, [loadSong, play]);

  const setVolume = useCallback((volume: number) => {
    setState(prev => ({
      ...prev,
      volume: Math.max(0, Math.min(100, volume)),
    }));
  }, []);

  const seek = useCallback((time: number) => {
    if (!audioRef.current) return;

    audioRef.current.currentTime = time;
    setState(prev => ({
      ...prev,
      currentTime: time,
    }));
  }, []);

  const controls: AudioPlayerControls = {
    play,
    pause,
    stop,
    next,
    previous,
    setVolume,
    seek,
    loadPlaylist,
    loadSong,
  };

  return { state, controls, audioRef };
}
